import React from 'react';

import BadgeTradePointType from 'src/components/Badge/BadgeTradePointType';
import { getPropertyLink } from 'src/helpers/tradeHelpers';
import { TRADE_POINT_TYPE_COMMUNITY, TRADE_POINT_TYPE_RESIDUAL } from 'src/util/constants';
import UUID from 'src/components/UUID';

/**
 * Build a trade point label for inline use.
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {object} tradePoint
 * @returns {import('react').ReactNode|null} - formatted trade point label with React elements,
 * or null if required data is missing.
 */
export const buildTradePointInlineLabel = (intl, tradePoint) => {
  if (!intl || !tradePoint) { return null; }

  const { id, meter, type } = tradePoint || {};
  const { title, identifier } = meter || {};
  const propertyLink = getPropertyLink(tradePoint, false);

  const retailerLabelText = intl.formatMessage({
    id: 'util.i18n.trade_point.build_trade_point_inline_label.trade_point_type_residual.retailer.label',
    defaultMessage: 'Retailer',
  });

  const communityLabelText = intl.formatMessage({
    id: 'util.i18n.trade_point.build_trade_point_inline_label.trade_point_type.community.label',
    defaultMessage: 'Community',
  });
  const tradePointLabelText = intl.formatMessage({
    id: 'util.i18n.trade_point.build_trade_point_inline_label.trade_point.label',
    defaultMessage: 'Trade Point',
  });

  if (!id) {
    if (type === TRADE_POINT_TYPE_RESIDUAL) {
      return intl.formatMessage({
        id: 'util.i18n.trade_point.build_trade_point_inline_label.trade_point_type_residual.no_id.label',
        defaultMessage: '{retailerLabel}',
      }, {
        retailerLabel: <span className="trade-point-title">{retailerLabelText}</span>,
      });
    }

    if (type === TRADE_POINT_TYPE_COMMUNITY) {
      return intl.formatMessage({
        id: 'util.i18n.trade_point.build_trade_point_inline_label.trade_point_type.community.no_id.label',
        defaultMessage: '{communityLabel}',
      }, {
        communityLabel: <span className="trade-point-title">{communityLabelText}</span>,
      });
    }
    if (!meter) {
      return intl.formatMessage({
        id: 'util.i18n.trade_point.build_trade_point_inline_label.trade_point.no_id.label',
        defaultMessage: '{tradePointLabel}',
      }, {
        tradePointLabel: <span className="trade-point-title">{tradePointLabelText}</span>,
      });
    }

    if (!propertyLink) {
      return intl.formatMessage({
        id: 'util.i18n.trade_point.build_trade_point_inline_label.no_id_and_no_property.label',
        defaultMessage: '{meterTitle} ({identifier})',
      }, {
        identifier,
        meterTitle: <span className="trade-point-title">{title}</span>,
      });
    }

    return intl.formatMessage({
      id: 'util.i18n.trade_point.build_trade_point_inline_label.no_id_and_with_property.label',
      defaultMessage: '{meterTitle} ({identifier}) at {propertyLink}',
    }, {
      identifier,
      meterTitle: <span className="trade-point-title">{title}</span>,
      propertyLink,
    });
  }
  const copySuccessMsg = intl.formatMessage({
    id: 'trade_point.copy_action.success',
    defaultMessage: 'Trade point id copied to the clipboard',
  });
  const copyTitle = intl.formatMessage({
    id: 'util.i18n.trade_point.build_trade_point_inline_label.copy_action.label',
    defaultMessage: 'Copy trade point id to the clipboard',
  });

  const idLabel = (
    <>
      (
      <UUID
        copySuccessMsg={copySuccessMsg}
        copyTitle={copyTitle}
        uuid={id}
      />
      )
    </>
  );

  if (type === TRADE_POINT_TYPE_RESIDUAL) {
    return intl.formatMessage({
      id: 'util.i18n.trade_point.build_trade_point_inline_label.trade_point_type_residual.with_id.label',
      defaultMessage: '{retailerLabel} {idLabel}',
    }, {
      idLabel,
      retailerLabel: retailerLabelText,
    });
  }

  if (type === TRADE_POINT_TYPE_COMMUNITY) {
    return intl.formatMessage({
      id: 'util.i18n.trade_point.build_trade_point_inline_label.trade_point_type.community.with_id.label',
      defaultMessage: '{communityLabel} {idLabel}',
    }, {
      communityLabel: <span className="trade-point-title">{communityLabelText}</span>,
      idLabel,
    });
  }

  if (!meter) {
    return intl.formatMessage({
      id: 'util.i18n.trade_point.build_trade_point_inline_label.trade_point.with_id.label',
      defaultMessage: '{tradePointLabel} {idLabel}',
    }, {
      idLabel,
      tradePointLabel: <span className="trade-point-title">{tradePointLabelText}</span>,
    });
  }

  if (!propertyLink) {
    return intl.formatMessage({
      id: 'util.i18n.trade_point.build_trade_point_inline_label.with_id_and_no_property.label',
      defaultMessage: '{meterTitle} ({identifier}) {idLabel}',
    }, {
      identifier,
      idLabel,
      meterTitle: <span className="trade-point-title">{title}</span>,
    });
  }

  if (propertyLink) {
    return intl.formatMessage({
      id: 'util.i18n.trade_point.build_trade_point_inline_label.with_id_and_with_property.label',
      defaultMessage: '{meterTitle} ({identifier}) at {propertyLink} {idLabel}',
    }, {
      identifier,
      idLabel,
      meterTitle: <span className="trade-point-title">{title}</span>,
      propertyLink,
    });
  }
  return null;
};

/**
 * Build a trade point label for the trade rule table.
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {object} tradePoint
 * @returns {import('react').ReactNode|null} - trade point label.
 */
export const buildTradePointTradeRuleTableLabel = (intl, tradePoint) => {
  if (!intl || !tradePoint) { return null; }

  const { id, meter, type } = tradePoint || {};
  const { title, identifier } = meter || {};
  const propertyLink = getPropertyLink(tradePoint, false);
  const tradePointType = <BadgeTradePointType type={type} />;

  const retailerLabel = (
    <>
      <strong>
        {intl.formatMessage({
          id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.trade_point_type_residual.retailer.label',
          defaultMessage: 'Retailer',
        })}
      </strong>
      <br />
    </>
  );
  const communityLabel = (
    <>
      <strong>
        {intl.formatMessage({
          id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.trade_point_type_community.community.label',
          defaultMessage: 'Community',
        })}
      </strong>
      <br />
    </>
  );
  const tradePointLabel = (
    <>
      <strong>
        {intl.formatMessage({
          id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.trade_point.label',
          defaultMessage: 'Trade Point',
        })}
      </strong>
      <br />
    </>
  );

  if (!id) {
    if (type === TRADE_POINT_TYPE_RESIDUAL) {
      return (
        intl.formatMessage(
          {
            id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.trade_point_type_residual.no_id.label',
            defaultMessage: '{retailerLabel} {tradePointType}',
          },
          {
            retailerLabel,
            tradePointType,
          },
        )
      );
    }

    if (type === TRADE_POINT_TYPE_COMMUNITY) {
      return (
        intl.formatMessage(
          {
            id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.trade_point_type_community.no_id.label',
            defaultMessage: '{communityLabel} {tradePointType}',
          },
          {
            communityLabel,
            tradePointType,
          },
        )
      );
    }

    if (!meter) {
      return (
        intl.formatMessage(
          {
            id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.trade_point.no_id.label',
            defaultMessage: '{tradePointLabel} {tradePointType}',
          },
          {
            tradePointLabel,
            tradePointType,
          },
        )
      );
    }

    if (!propertyLink) {
      const meterIdentifier = (
        <>
          {`(${identifier})`}
          <br />
        </>
      );
      return (
        intl.formatMessage(
          {
            id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.no_id_and_no_property.label',
            defaultMessage: '{meterTitle} {meterIdentifier} {tradePointType}',
          },
          {
            meterIdentifier,
            meterTitle: <strong>{title}</strong>,
            tradePointType,
          },
        )
      );
    }

    const meterIdentifier = (
      <>
        {`(${identifier})`}
        <br />
      </>
    );
    const finalPropertyLink = (
      <>
        {propertyLink}
        <br />
      </>
    );
    return (
      intl.formatMessage(
        {
          id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.no_id_and_with_property.label',
          defaultMessage: '{meterTitle} {meterIdentifier} {propertyLink} {tradePointType}',
        },
        {
          meterIdentifier,
          meterTitle: <strong>{title}</strong>,
          tradePointType,
          propertyLink: finalPropertyLink,
        },
      )
    );
  }
  const copyTitle = intl.formatMessage({
    id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.copy_action.label',
    defaultMessage: 'Copy counterparty id to the clipboard',
  });
  const copySuccessMsg = intl.formatMessage({
    id: 'trade_point.copy_action.success',
    defaultMessage: 'Counterparty Trade point id copied to the clipboard',
  });
  const idLabel = (
    <>
      (
      <UUID copyTitle={copyTitle} copySuccessMsg={copySuccessMsg} uuid={id} />
      )
      <br />
    </>
  );

  if (type === TRADE_POINT_TYPE_RESIDUAL) {
    return (
      intl.formatMessage(
        {
          id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.trade_point_type_residual.with_id.label',
          defaultMessage: '{retailerLabel} {idLabel} {tradePointType}',
        },
        {
          idLabel,
          retailerLabel,
          tradePointType,
        },
      )
    );
  }

  if (type === TRADE_POINT_TYPE_COMMUNITY) {
    return (
      intl.formatMessage(
        {
          id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.trade_point_type_community.with_id.label',
          defaultMessage: '{communityLabel} {idLabel} {tradePointType}',
        },
        {
          communityLabel,
          idLabel,
          tradePointType,
        },
      )
    );
  }

  if (!meter) {
    return (
      intl.formatMessage(
        {
          id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.trade_point.with_id.label',
          defaultMessage: '{tradePointLabel} {idLabel} {tradePointType}',
        },
        {
          idLabel,
          tradePointLabel,
          tradePointType,
        },
      )
    );
  }

  if (!propertyLink) {
    const meterIdentifier = (
      <>
        {`(${identifier})`}
        <br />
      </>
    );
    return (
      intl.formatMessage(
        {
          id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.with_id_and_no_property.label',
          defaultMessage: '{meterTitle} {meterIdentifier} {idLabel} {tradePointType}',
        },
        {
          meterIdentifier,
          idLabel,
          meterTitle: <strong>{title}</strong>,
          tradePointType,
        },
      )
    );
  }

  if (propertyLink) {
    const meterIdentifier = (
      <>
        {`(${identifier})`}
        <br />
      </>
    );
    const finalPropertyLink = (
      <>
        {propertyLink}
        <br />
      </>
    );
    return (
      intl.formatMessage(
        {
          id: 'util.i18n.trade_point.build_trade_point_trade_rule_table_label.with_id_and_with_property.label',
          defaultMessage: '{meterTitle} {meterIdentifier} {propertyLink} {idLabel} {tradePointType}',
        },
        {
          meterIdentifier,
          idLabel,
          meterTitle: <strong>{title}</strong>,
          propertyLink: finalPropertyLink,
          tradePointType,
        },
      )
    );
  }

  return null;
};
